import { usePageHeader } from "contexts/PageHeaderContext";
import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "ui/card";
import { Button } from "ui/button";
import {
  Save,
  FileText,
  FileTerminal,
  CalendarDays,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "ui/tabs";
import CustomCKEditor from '../../components/editor/ckEditor'
import { FullScreenLoading } from "components/ui/loader";
import { GeneralConfigurationPayload, getGeneralConfiguration, updateGeneralConfiguration } from "services/masterConfigurationService";
import { useAuth } from "contexts/AuthContext";
import { getTourOperatorTypes } from "services/tourOperatorTypeService";
import { Label } from "ui/label";
import { Input } from "ui/input";

export interface GeneralConfigurationItem {
  id: number;
  code: string;
  value: string;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

type TourTncData = {
  instant: string;
  faiz: string;
  miqaat: string;
  hotel: string;
};

type QarzanTncData = {
  termandconditions: string;
};

type PropertyAvailabilityReleaseRule = {
  id: string;
  toTypeId: string;
  toTypeName: string;
  releaseDays: string;
};

type PropertyAvailabilityData = {
  releaseRules: PropertyAvailabilityReleaseRule[];
};

type TOTypeOption = {
  id: string;
  name: string;
};

export function GeneralMasterPage() {
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "General Master",
      childTitle: "Configure global system settings and limits",
      icon: <FileTerminal className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const { user } = useAuth();
  const [loading, setLoading] = useState(false);


  const [tours, setTours] = useState<TourTncData>({
    instant: "",
    faiz: "",
    miqaat: "",
    hotel: "",
  });

  const [qarzan, setQarzan] = useState<QarzanTncData>({
    termandconditions: "",
  });


  const [propertyAvailability, setPropertyAvailability] = useState<PropertyAvailabilityData>({ releaseRules: [], });
  const [toTypeOptions, setToTypeOptions] = useState<TOTypeOption[]>([]);


  const [errors, setErrors] = useState({
    tours: {
      instant: "",
      faiz: "",
      miqaat: "",
      hotel: "",
    },
    qarzan: {
      termandconditions: "",
    },
    propertyAvailability: {
      releaseDays: "",
    },
  });


  // MAPPING GET API RESPONSE TO STATE KEYS START
  const mapTourCodeToKey = (code: string): keyof TourTncData | null => {
    const key = code.split("_").pop()?.toLowerCase();

    const validKeys: (keyof TourTncData)[] = [
      "instant",
      "faiz",
      "miqaat",
      "hotel",
    ];

    return validKeys.includes(key as keyof TourTncData)
      ? (key as keyof TourTncData)
      : null;
  };


  const mapQarzanCodeToKey = (code: string): keyof QarzanTncData | null => {
    const key = code.split("_").pop()?.toLowerCase();

    return key === "termandconditions"
      ? "termandconditions"
      : null;
  };


  const parseReleaseRules = (value: string): PropertyAvailabilityReleaseRule[] => {
    try {
      const parsed = JSON.parse(value || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const addReleaseRule = () => {
    const firstAvailableType = toTypeOptions.find(
      (option) =>
        !propertyAvailability.releaseRules.some(
          (rule) => rule.toTypeId === option.id,
        ),
    );

    setPropertyAvailability((prev) => ({
      releaseRules: [
        ...prev.releaseRules,
        {
          id: `release-rule-${Date.now()}`,
          toTypeId: firstAvailableType?.id || "",
          toTypeName: firstAvailableType?.name || "",
          releaseDays: "",
        },
      ],
    }));
  };

  const updateReleaseRule = (
    id: string,
    field: keyof PropertyAvailabilityReleaseRule,
    value: string,
  ) => {
    setPropertyAvailability((prev) => ({
      releaseRules: prev.releaseRules.map((rule) => {
        if (rule.id !== id) return rule;

        if (field === "toTypeId") {
          const selectedType = toTypeOptions.find((option) => option.id === value);
          return {
            ...rule,
            toTypeId: value,
            toTypeName: selectedType?.name || "",
          };
        }

        return { ...rule, [field]: value };
      }),
    }));

    setErrors((prev) => ({
      ...prev,
      propertyAvailability: { releaseDays: "" },
    }));
  };

  const removeReleaseRule = (id: string) => {
    setPropertyAvailability((prev) => ({
      releaseRules: prev.releaseRules.filter((rule) => rule.id !== id),
    }));
  };

  const getGeneralConfigurationData = useCallback(async () => {
    try {
      setLoading(true);

      const response: GeneralConfigurationItem[] = await getGeneralConfiguration();

      const toursData: TourTncData = {
        instant: "",
        faiz: "",
        miqaat: "",
        hotel: "",
      };

      const qarzanData: QarzanTncData = {
        termandconditions: "",
      };

      const propertyAvailabilityData: PropertyAvailabilityData = {
        releaseRules: [],
      };

      response?.forEach((item) => {
        if (item.code.startsWith("TOUR_TNC_")) {
          const key = mapTourCodeToKey(item.code);
          if (key) toursData[key] = item.value;
        }

        if (item.code.startsWith("QARZAN_TNC_")) {
          const key = mapQarzanCodeToKey(item.code);
          if (key) qarzanData[key] = item.value;
        }

        if (item.code === "PROPERTY_AVAILABILITY_RELEASE_DAYS_BY_TO_TYPE") {
          propertyAvailabilityData.releaseRules = parseReleaseRules(item.value);
        }

        if (
          item.code === "PROPERTY_AVAILABILITY_RELEASE_DAYS" &&
          !propertyAvailabilityData.releaseRules.length
        ) {
          propertyAvailabilityData.releaseRules = [
            {
              id: "legacy-release-days",
              toTypeId: "",
              toTypeName: "",
              releaseDays: item.value || "",
            },
          ];
        }
      });

      setTours(toursData);
      setQarzan(qarzanData);
      setPropertyAvailability(propertyAvailabilityData);
    } catch {
      toast.error("Failed to fetch master configuration details");
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    getGeneralConfigurationData()
  }, [getGeneralConfigurationData]);



  useEffect(() => {
    const loadTOTypeOptions = async () => {
      try {
        const res = await getTourOperatorTypes({
          searchText: null,
          typeName: null,
          code: null,
          category: null,
          isActive: true,
          pageNo: 1,
          pageSize: 100,
        });

        setToTypeOptions(
          res.items.map((item) => ({
            id: String(item.tourOperatorTypeId),
            name: item.typeName,
          })),
        );
      } catch {
        setToTypeOptions([
          { id: "in-house", name: "In-house" },
          { id: "external", name: "External" },
        ]);
      }
    };

    loadTOTypeOptions();
  }, []);
  // MAPPING GET API RESPONSE TO STATE KEYS END


  const handleSave = async () => {

    // editor validation start
    const newErrors = {
      tours: {
        instant: "",
        faiz: "",
        miqaat: "",
        hotel: "",
      },
      qarzan: {
        termandconditions: "",
      },
      propertyAvailability: {
        releaseDays: "",
      },
    };

    let hasError = false;

    // Tours
    if (!tours.instant.trim()) {
      newErrors.tours.instant = "Instant is required";
      hasError = true;
    }

    if (!tours.faiz.trim()) {
      newErrors.tours.faiz = "Faiz is required";
      hasError = true;
    }

    if (!tours.miqaat.trim()) {
      newErrors.tours.miqaat = "Miqaat is required";
      hasError = true;
    }

    if (!tours.hotel.trim()) {
      newErrors.tours.hotel = "Hotel is required";
      hasError = true;
    }

    // Qarzan
    if (!qarzan.termandconditions.trim()) {
      newErrors.qarzan.termandconditions = "Term and Conditions is required";
      hasError = true;
    }


    // property availability
    const invalidReleaseRule = propertyAvailability.releaseRules.some(
      (rule) => !rule.toTypeId || !rule.releaseDays || Number(rule.releaseDays) < 0,
    );

    if (invalidReleaseRule) {
      newErrors.propertyAvailability.releaseDays =
        "Select TO Type and enter valid release days for every row";
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) {
      toast.error("Please fill all required fields");
      return;
    }
    // editor validation ends

    try {
      setLoading(true);

      const payload: GeneralConfigurationPayload = {
        updatedBy: Number(user?.id),
        //tour payload
        TOUR_TNC_Instant: tours.instant,
        TOUR_TNC_Hotel: tours.hotel,
        TOUR_TNC_Miqaat: tours.miqaat,
        TOUR_TNC_Faiz: tours.faiz,

        // qardan payload
        QARZAN_TNC_TermAndConditions: qarzan.termandconditions,

        // property payload
        PROPERTY_AVAILABILITY_RELEASE_DAYS:
          propertyAvailability.releaseRules[0]?.releaseDays || "0",
        PROPERTY_AVAILABILITY_RELEASE_DAYS_BY_TO_TYPE: JSON.stringify(
          propertyAvailability.releaseRules.map((rule) => ({
            ...rule,
            releaseDays: String(rule.releaseDays || "0"),
          })),
        ),
      };

      setLoading(true);
      const response = await updateGeneralConfiguration(payload);
      toast.success(
        response?.message || "General settings updated successfully!"
      );
    } catch (error: unknown) {
      toast.error(
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message || "General settings updated successfully!"
      );
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="space-y-4 sm:space-y-6 pb-8">
      {loading && <FullScreenLoading />}
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <Button
            onClick={handleSave}
            style={{ background: "#5B9BD5" }}
            className="text-white flex-1 sm:flex-none text-xs sm:text-sm"
            size="sm"
          >
            <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
            Save
          </Button>
        </div>
      </div>


      {/* Tabs for different modules */}
      <Tabs defaultValue="tours" className="w-full space-y-6">
        <TabsList className="grid h-auto w-full grid-cols-1 sm:grid-cols-3 rounded-xl border border-[#5B9BD5]/20 bg-sky-50/80 p-1 gap-1">
          <TabsTrigger
            value="tours"
            className="flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#5B9BD5]"
          >
            Tours
          </TabsTrigger>

          <TabsTrigger
            value="qarzan-hasana"
            className="flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#5B9BD5]"
          >
            Qardan Hasana
          </TabsTrigger>

          <TabsTrigger
            value="property-availability"
            className="flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#5B9BD5]"
          >
            Property Availability
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tours" className="space-y-4 sm:space-y-6">
          {/* Terms & Conditions - Mobile Responsive */}
          <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px" }}>
            <CardHeader
              className="rounded-t-xl border-b bg-gradient-to-r from-sky-50 to-blue-50 p-4 sm:p-6"
              style={{ gap: "0px" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#5B9BD5] flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <CardTitle
                    className="text-sm sm:text-base"
                    style={{ color: "#5B9BD5" }}
                  >
                    Terms & Conditions
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Add fixed text to display on tours
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-2 gap-4 flex flex-col">
                <Card className="bg-white p-6">
                  <CustomCKEditor
                    label="Instant "
                    required
                    value={tours.instant}
                    onChange={(value) =>
                      setTours((prev) => ({
                        ...prev,
                        instant: value,
                      }))
                    }
                    error={errors.tours.instant}
                  />
                </Card>

                <Card className="bg-white p-6">
                  <CustomCKEditor
                    label="Faiz"
                    required
                    value={tours.faiz}
                    onChange={(value) =>
                      setTours((prev) => ({
                        ...prev,
                        faiz: value,
                      }))
                    }
                    error={errors.tours.faiz}
                  />
                </Card>

                <Card className="bg-white p-6">
                  <CustomCKEditor
                    label="Hotel"
                    required
                    value={tours.hotel}
                    onChange={(value) =>
                      setTours((prev) => ({
                        ...prev,
                        hotel: value,
                      }))
                    }
                    error={errors.tours.hotel}
                  />
                </Card>

                <Card className="bg-white p-6">
                  <CustomCKEditor
                    label="Miqaat"
                    required
                    value={tours.miqaat}
                    onChange={(value) =>
                      setTours((prev) => ({
                        ...prev,
                        miqaat: value,
                      }))
                    }
                    error={errors.tours.miqaat}
                  />
                </Card>

              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB CONTENT FOR QARDAN HASANA */}
        <TabsContent value="qarzan-hasana" className="space-y-4 sm:space-y-6">
          <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px" }}>
            <CardHeader
              className="rounded-t-xl border-b bg-gradient-to-r from-emerald-50 to-green-50 p-4 sm:p-6"
              style={{ gap: "0px" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-sm sm:text-base text-emerald-600">
                    Terms & Conditions
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Configure text to display in Qardan Hasana
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-2 gap-4 flex flex-col">
                <Card className="bg-white p-6">
                  <CustomCKEditor
                    label="Term and Conditions"
                    required
                    value={qarzan.termandconditions}
                    onChange={(value) =>
                      setQarzan((prev) => ({
                        ...prev,
                        termandconditions: value,
                      }))
                    }
                    error={errors.qarzan.termandconditions}
                  />
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* TAB CONTENT FOR QARDAN HASANA */}

        {/* TAB CONTENT PROPERTY AVAILABILITY */}
        <TabsContent value="property-availability" className="space-y-4 sm:space-y-6">
          <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px" }}>
            <CardHeader
              className="rounded-t-xl border-b bg-gradient-to-r from-sky-50 to-blue-50 p-4 sm:p-6"
              style={{ gap: "0px" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#5B9BD5] flex items-center justify-center flex-shrink-0">
                  <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <CardTitle
                    className="text-sm sm:text-base"
                    style={{ color: "#5B9BD5" }}
                  >
                    Property Availability
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Configure availability release rules for properties
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      TO Type Wise Release Days
                    </h3>
                    <p className="text-xs text-gray-500">
                      Configure separate release days for each Tour Operator Type.
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={addReleaseRule}
                    style={{ background: "#5B9BD5" }}
                    className="text-white"
                  >
                    Add Row
                  </Button>
                </div>

                {errors.propertyAvailability.releaseDays && (
                  <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">
                    {errors.propertyAvailability.releaseDays}
                  </p>
                )}

                {propertyAvailability.releaseRules.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-6 text-center text-sm text-gray-500">
                    No release days configured. Click Add Row to create one.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {propertyAvailability.releaseRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="grid grid-cols-1 gap-3 rounded-lg border bg-white p-4 md:grid-cols-[1fr_180px_auto] md:items-end"
                      >
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700">
                            TO Type
                          </Label>
                          <select
                            value={rule.toTypeId}
                            onChange={(event) =>
                              updateReleaseRule(rule.id, "toTypeId", event.target.value)
                            }
                            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                          >
                            <option value="">Select TO Type</option>
                            {toTypeOptions.map((option) => (
                              <option
                                key={option.id}
                                value={option.id}
                                disabled={propertyAvailability.releaseRules.some(
                                  (existingRule) =>
                                    existingRule.id !== rule.id &&
                                    existingRule.toTypeId === option.id,
                                )}
                              >
                                {option.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700">
                            Release Days
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={rule.releaseDays}
                            onChange={(event) =>
                              updateReleaseRule(rule.id, "releaseDays", event.target.value)
                            }
                            placeholder="e.g., 7"
                          />
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          className="md:mb-0"
                          onClick={() => removeReleaseRule(rule.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* Tab content property availability */}
      </Tabs>
    </div>
  );
}
